import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Shield, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { validatePassword, getPasswordStrength } from '@/lib/validations';
import { Progress } from '@/components/ui/progress';

interface InvitationData {
  id: string;
  email: string;
  full_name: string;
  global_role: string;
  invited_by: string;
  expires_at: string;
  inviter_name?: string;
}

const AcceptGlobalInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const passwordValidation = validatePassword(password);
  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    if (!token) return;

    try {
      const { data, error } = await supabase
        .from('global_admin_invitations')
        .select('*')
        .eq('id', token)
        .eq('status', 'sent')
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        toast({
          title: 'Convite inválido',
          description: 'Este convite não existe ou já expirou',
          variant: 'destructive'
        });
        navigate('/login');
        return;
      }

      // Buscar nome do convidante
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', data.invited_by)
        .single();

      setInvitation({
        ...data,
        inviter_name: inviterProfile?.full_name || 'Sistema'
      });
    } catch (error) {
      console.error('Error loading invitation:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar convite',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!invitation) return;

    if (!passwordValidation.isValid) {
      toast({
        title: 'Senha inválida',
        description: passwordValidation.errors[0],
        variant: 'destructive'
      });
      return;
    }

    if (!passwordsMatch) {
      toast({
        title: 'Erro',
        description: 'As senhas não coincidem',
        variant: 'destructive'
      });
      return;
    }

    setAccepting(true);

    try {
      // Chamar edge function para processar tudo com service_role
      const { data, error } = await supabase.functions.invoke('accept-global-invitation', {
        body: {
          invitationId: invitation.id,
          password: password
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao aceitar convite');

      console.log('✅ Invitation accepted via edge function:', data);

      // Fazer login com as credenciais criadas
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: password
      });

      if (signInError) {
        console.error('Sign in error:', signInError);
        // Mesmo que falhe o login automático, a conta foi criada
        toast({
          title: 'Conta criada!',
          description: 'Sua conta foi criada. Por favor, faça login.',
        });
        navigate('/login');
        return;
      }

      toast({
        title: 'Bem-vindo!',
        description: 'Sua conta foi criada com sucesso. Redirecionando...'
      });

      setTimeout(() => {
        navigate('/admin');
      }, 1500);

    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({
        title: 'Erro',
        description: (error as Error).message || 'Erro ao aceitar convite',
        variant: 'destructive'
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="inline-flex p-4 rounded-full bg-primary/10 mx-auto">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl">Convite para Admin Global</CardTitle>
            <CardDescription className="mt-2">
              Você foi convidado por <strong>{invitation.inviter_name}</strong>
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="space-y-3 p-4 bg-muted rounded-lg">
            <div>
              <Label className="text-xs text-muted-foreground">Email</Label>
              <p className="font-medium">{invitation.email}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Nome Completo</Label>
              <p className="font-medium">{invitation.full_name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Papel</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Shield className="h-4 w-4 text-primary" />
                <span className="font-medium">Administrador Global</span>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Crie uma senha segura para sua conta:
            </p>

            <div className="space-y-2">
              <Label htmlFor="password">Senha *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite uma senha segura"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {password && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Força da senha:</span>
                    <span className={`font-medium ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <Progress value={(passwordStrength.score / 4) * 100} className="h-2" />
                </div>
              )}

              {password && !passwordValidation.isValid && (
                <Alert variant="destructive" className="py-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <ul className="list-disc list-inside space-y-1 mt-1">
                      {passwordValidation.errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Senha *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite a senha novamente"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-destructive">As senhas não coincidem</p>
              )}
              
              {confirmPassword && passwordsMatch && (
                <p className="text-xs text-green-600 flex items-center">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Senhas coincidem
                </p>
              )}
            </div>
          </div>

          <Button
            onClick={handleAccept}
            disabled={accepting || !passwordValidation.isValid || !passwordsMatch}
            className="w-full"
            size="lg"
          >
            {accepting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Criando conta...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Aceitar Convite e Criar Conta
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Ao aceitar, você terá acesso total ao painel administrativo do Portal ONE Suite
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptGlobalInvitation;
